import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, takeUntil, finalize, switchMap, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MatExpansionPanel } from '@angular/material/expansion';

import { IdentityDocument } from '@app/core/types/client-document';
import { IdentityReportsService } from '@app/core/services/identity-reports.service';
import { NotificationService } from '@app/core/services/notification.service';
import { BrandCodes } from '@app/core/types/brand';
import {
  CreateVerificationDto,
  DOCUMENT_STATE,
  DOCUMENT_STATE_CLASS,
  IdentityVerification,
  parseDocumentState,
  VERIFICATION_FLOW,
  VERIFICATION_FLOW_LABEL,
  SlotKey,
  SLOT_CONFIGS,
  IdCardImageTypes,
  FLOW_OPTIONS,
  VerificationDecision,
  StartVerificationDialogData,
} from '@app/core/types/identity-verification';
import { IdentityDocumentBlobCacheService } from '@app/core/services/identity-reports-blob-cache.service';
import { TilePreviewDialogData, TilePreviewDialogComponent } from './tile-preview-dialog.component';
interface TileVM {
  id: number;
  name: string;
  state: DOCUMENT_STATE;
  stateClass: 'valid' | 'invalid' | 'pending' | 'validating' | 'accepted';
  reasonLabel: string;
  isFromPrev: boolean;
  imageUrl: string | null;
  loading: boolean;
  error: boolean;
}

interface SlotControl {
  key: SlotKey;
  formControlName: 'frontId' | 'backId' | 'selfieId';
  label: string;
  apiType: IdCardImageTypes;
  hasOptions: boolean;
}

const DISPLAY_ORDER: SlotKey[] = ['selfie', 'front', 'back'];

@Component({
  selector: 'itfg-start-identity-verification-dialog',
  templateUrl: './start-identity-verification-dialog.component.html',
  styleUrl: './start-identity-verification-dialog.component.scss',
  standalone: false,
})
export class StartVerificationDialogComponent implements OnInit, OnDestroy {
  readonly FLOW = VERIFICATION_FLOW;
  readonly FLOW_LABEL = VERIFICATION_FLOW_LABEL;
  readonly FLOW_OPTIONS = FLOW_OPTIONS;

  form!: FormGroup;
  slotControls: SlotControl[] = [];
  tilesByKey: Record<SlotKey, TileVM[]> = {} as Record<SlotKey, TileVM[]>;
  cursorByKey: Record<SlotKey, number> = {} as Record<SlotKey, number>;

  docList: IdentityDocument[] = [];
  loadingDocs = false;
  isLoading = false;

  private readonly ownedUrls = new Set<string>();
  private readonly unsubscribe = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    readonly dialogRef: MatDialogRef<
      StartVerificationDialogComponent,
      IdentityVerification | null
    >,
    @Inject(MAT_DIALOG_DATA) readonly data: StartVerificationDialogData,
    private readonly service: IdentityReportsService,
    private readonly blobCache: IdentityDocumentBlobCacheService,
    private readonly notificationService: NotificationService,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.form = this.createForm();
    this.loadDocs();
  }

  loadDocs(): void {
    this.loadingDocs = true;

    this.service
      .getIdentityDocumentList$(
        this.data.client.civilId,
        this.data.client.brand.code as BrandCodes
      )
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => (this.loadingDocs = false))
      )
      .subscribe({
        next: (docs: IdentityDocument[]) => {
          this.docList = docs;
          this.buildSlotControls();
          this.buildTiles();
          this.applyDefaultSelections();
          this.preloadInitialTiles();
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'verification.id.loadDocsError',
          });
        },
      });
  }

  buildSlotControls(): void {
    this.slotControls = DISPLAY_ORDER.map((key) => {
      const cfg = SLOT_CONFIGS[key];
      const matchingDocs = this.docList.filter(
        (d) => d.type?.name === cfg.apiType
      );
      return {
        key,
        formControlName:
          key === 'front' ? 'frontId' : key === 'back' ? 'backId' : 'selfieId',
        label: cfg.label,
        apiType: cfg.apiType,
        hasOptions: matchingDocs.length > 0,
      };
    });

    for (const slot of this.slotControls) {
      const ctrl = this.form.get(slot.formControlName);
      if (!slot.hasOptions) ctrl?.disable({ emitEvent: false });
      else ctrl?.enable({ emitEvent: false });
    }
  }

  buildTiles(): void {
    const prev = this.data.previousVerification;
    for (const slot of this.slotControls) {
      const cfg = SLOT_CONFIGS[slot.key];
      const prevDocId = (prev?.[cfg.dtoKey] as number | null) ?? null;
      const prevStateRaw = (prev?.[cfg.stateKey] as string | null) ?? null;

      const matchingDocs = this.docList
        .filter((d) => d.type?.name === cfg.apiType)
        .sort((a, b) => b.id - a.id);

      this.tilesByKey[slot.key] = matchingDocs.map((doc) => {
        const isFromPrev = doc.id === prevDocId;
        const parsed = parseDocumentState(isFromPrev ? prevStateRaw : null);

        return {
          id: doc.id,
          name: doc.name || `Документ #${doc.id}`,
          state: parsed.base,
          stateClass: DOCUMENT_STATE_CLASS[parsed.base],
          reasonLabel: parsed.reason
            ? this.translate.instant(
                `verification.id.documentActions.changeState.${parsed.reason}`
              )
            : '',
          isFromPrev,
          imageUrl: null,
          loading: false,
          error: false,
        };
      });

      this.cursorByKey[slot.key] = 0;
    }
  }

  applyDefaultSelections(): void {
    const prev = this.data.previousVerification;
    if (!prev) return;

    for (const slot of this.slotControls) {
      const cfg = SLOT_CONFIGS[slot.key];
      const prevDocId = (prev[cfg.dtoKey] as number | null) ?? null;
      const prevStateRaw = (prev[cfg.stateKey] as string | null) ?? null;

      if (!prevDocId) continue;

      const parsed = parseDocumentState(prevStateRaw);
      if (parsed.base === DOCUMENT_STATE.REJECTED) continue;
      if (parsed.base === DOCUMENT_STATE.MISSING) continue;

      const tiles = this.tilesByKey[slot.key] ?? [];
      const idx = tiles.findIndex((t) => t.id === prevDocId);
      if (idx >= 0) {
        this.form.get(slot.formControlName)?.setValue(prevDocId);
        // Position the cursor on the auto-selected tile
        this.cursorByKey[slot.key] = idx;
      }
    }
  }

  /** Load the current tile (cursor position) for each slot. */
  private preloadInitialTiles(): void {
    for (const key of DISPLAY_ORDER) {
      const tile = this.getCurrentTile(key);
      if (!tile) continue;

      // Try cache first for instant render
      const cached = this.blobCache.peek(this.data.client.civilId, tile.id);
      if (cached) {
        this.materializeFromCache(tile);
        continue;
      }
      this.loadTile(tile);
    }
  }

  getCurrentTile(key: SlotKey): TileVM | null {
    const tiles = this.tilesByKey[key];
    if (!tiles || tiles.length === 0) return null;
    const idx = this.cursorByKey[key] ?? 0;
    return tiles[idx] ?? null;
  }

  prevTile(key: SlotKey): void {
    const tiles = this.tilesByKey[key] ?? [];
    if (tiles.length <= 1) return;
    const cur = this.cursorByKey[key] ?? 0;
    if (cur === 0) return;
    this.cursorByKey[key] = cur - 1;
    this.ensureCurrentLoaded(key);
  }

  nextTile(key: SlotKey): void {
    const tiles = this.tilesByKey[key] ?? [];
    if (tiles.length <= 1) return;
    const cur = this.cursorByKey[key] ?? 0;
    if (cur >= tiles.length - 1) return;
    this.cursorByKey[key] = cur + 1;
    this.ensureCurrentLoaded(key);
  }

  private ensureCurrentLoaded(key: SlotKey): void {
    const tile = this.getCurrentTile(key);
    if (!tile) return;
    if (tile.imageUrl || tile.loading) return;

    const cached = this.blobCache.peek(this.data.client.civilId, tile.id);
    if (cached) {
      this.materializeFromCache(tile);
      return;
    }
    this.loadTile(tile);
  }

  onTileClick(slot: SlotControl, tile: TileVM): void {
    const ctrl = this.form.get(slot.formControlName);
    const current = ctrl?.value as number | null;
    const next = current === tile.id ? null : tile.id;
    ctrl?.setValue(next);
  }

  openPreview(tile: TileVM, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    const data: TilePreviewDialogData = {
      civilId: this.data.client.civilId,
      docId: tile.id,
      docName: tile.name,
    };

    this.dialog.open(TilePreviewDialogComponent, {
      data,
      width: '720px',
      height: '90vh',
      maxWidth: '90vw',
      panelClass: 'tile-preview-dialog',
      autoFocus: false,
    });
  }

  isSelected(slot: SlotControl, tile: TileVM): boolean {
    return this.form.get(slot.formControlName)?.value === tile.id;
  }

  private loadTile(tile: TileVM): void {
    if (tile.imageUrl || tile.loading) return;

    tile.loading = true;
    tile.error = false;

    this.blobCache
      .get$(this.data.client.civilId, tile.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          this.ownedUrls.add(url);
          tile.imageUrl = url;
          tile.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          console.error('[StartVerificationDialog] tile load error', err);
          tile.loading = false;
          tile.error = true;
          this.cdr.markForCheck();
        },
      });
  }

  private materializeFromCache(tile: TileVM): void {
    const blob = this.blobCache.peek(this.data.client.civilId, tile.id);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    this.ownedUrls.add(url);
    tile.imageUrl = url;
  }

  get selectedSlotsCount(): number {
    let n = 0;
    if (this.form?.get('frontId')?.value) n++;
    if (this.form?.get('backId')?.value) n++;
    if (this.form?.get('selfieId')?.value) n++;
    return n;
  }

  onSubmit(): void {
    const flow: VERIFICATION_FLOW = this.form.value.flow;
    const frontId = this.form.value.frontId as number | null;
    const backId = this.form.value.backId as number | null;
    const selfieId = this.form.value.selfieId as number | null;

    const documents: CreateVerificationDto['documents'] = {};
    if (frontId) documents.idFrontId = frontId;
    if (backId) documents.idBackId = backId;
    if (selfieId) documents.selfieId = selfieId;

    const dto: CreateVerificationDto = {
      civilId: this.data.client.civilId,
      brand: this.data.client.brand.code as BrandCodes,
      flow,
      documents: Object.keys(documents).length ? documents : undefined,
    };

    this.isLoading = true;
    let createdVerification: IdentityVerification;

    const activeVerification = this.data.activeVerification;
    const invalidateActive$ = activeVerification
      ? this.service.changeVerificationStatus$(
          activeVerification.id,
          VerificationDecision.FAIL
        )
      : of(null);

    invalidateActive$
      .pipe(
        switchMap(() => this.service.createVerification(dto)),
        switchMap((verification: IdentityVerification) => {
          createdVerification = verification;
          return this.service.setVerificationInProgress(verification.id);
        }),
        takeUntil(this.unsubscribe),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: () => {
          this.notificationService.showLocalizedSuccessMessage({
            notificationText: 'verification.id.startVerificationSuccess',
          });
          this.dialogRef.close(createdVerification);
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'verification.id.startVerificationError',
          });
        },
      });
  }

  createForm(): FormGroup {
    return this.fb.group({
      flow: [VERIFICATION_FLOW.INTERNAL],
      frontId: [null as number | null],
      backId: [null as number | null],
      selfieId: [null as number | null],
    });
  }

  onFlowChange(flow: VERIFICATION_FLOW, panel: MatExpansionPanel): void {
    if (flow !== VERIFICATION_FLOW.INTERNAL) {
      this.form.patchValue(
        { frontId: null, backId: null, selfieId: null },
        { emitEvent: false }
      );
      panel.close();
    }
  }

  onNoClick(): void {
    this.dialogRef.close(null);
  }

  trackSlot = (_: number, s: SlotControl) => s.key;

  ngOnDestroy(): void {
    for (const url of this.ownedUrls) URL.revokeObjectURL(url);
    this.ownedUrls.clear();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
